import React from 'react';
import moment from 'moment';

import clsx from 'clsx';
import { Box, makeStyles } from '@material-ui/core';
import { Theme, createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { Controller, useFormState, Control, RegisterOptions, FieldError } from 'react-hook-form';
import Text from '../Text';
import Calendar from 'src/assets/images/calendar.svg';

const theme = createMuiTheme({
  overrides: {
    MuiFormControl: {
      root: {
        width: '100%'
      }
    },
    MuiOutlinedInput: {
      root: {
        '& $notchedOutline': {
          borderColor: '#EAEAEA'
        },
        '&:hover $notchedOutline': {
          borderColor: '#EAEAEA'
        }
      },
      adornedEnd: {
        paddingRight: 0
      }
    },
    MuiFormHelperText: {
      root: {
        '&.Mui-error': {
          color: '#D72D30'
        }
      },
      contained: {
        marginLeft: 0
      }
    }
  }
});

const useStyles = makeStyles((theme: Theme) => ({
  input: {
    padding: `${theme.spacing(1.5)}px ${theme.spacing(1)}px`,
    fontSize: 12,
    lineHeight: '20px',
    [theme.breakpoints.up('md')]: {
      fontSize: 14
    }
  },
  label: {
    lineHeight: '20px',
    display: 'block',
    fontSize: 12,
    fontWeight: 500,
    [theme.breakpoints.up('md')]: {
      fontSize: 14
    }
  },
  error: {
    fontWeight: 400,
    fontSize: 12,
    lineHeight: '20px',
    color: theme.palette2.text.danger
  },
  danger: {
    border: `1px solid ${theme.palette2.border.danger}`,
    borderRadius: 4
  }
}));

interface AppDatePickerProps {
  id: string;
  label?: string;
  name: string;
  validation?: RegisterOptions;
  error?: FieldError | any;
  control: Control<any>;
  format?: string;
  inputVariant?: 'standard' | 'outlined' | 'filled';
  leftArrowIcon?: React.ReactNode;
  rightArrowIcon?: React.ReactNode;
  leftArrowButtonProps?: object;
  rightArrowButtonProps?: object;
  allowKeyboardControl?: boolean;
  animateYearScrolling?: boolean;
  autoOk?: boolean;
  disabled?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  disableToolbar?: boolean;
  invalidDateMessage?: string;
  KeyboardButtonProps?: object;
  keyboardIcon?: React.ReactNode;
  maxDate?: string; // yyyy-mm-dd
  maxDateMessage?: string;
  minDate?: string; // yyyy-mm-dd
  minDateMessage?: string;
  onMonthChange?: (date: any) => void;
  onYearChange?: (date: any) => void;
  openTo?: 'date' | 'year' | 'month';
  orientation?: 'portrait' | 'landscape';
  readOnly?: boolean;
  shouldDisableDate?: (day: any) => boolean;
  variant?: 'dialog' | 'inline' | 'static';
  cancelLabel?: React.ReactNode;
  clearable?: boolean;
  clearLabel?: string;
  DialogProps?: object;
  okLabel?: string;
  showTodayButton?: boolean;
  todayLabel?: string;
  disabledDates?: string[]; // [ 'yyyy-mm-dd' ]
  py?: any;
  showErrorOnTouch?: boolean;
  showErrorOnSubmit?: boolean;
}

const AppDatePicker: React.FC<AppDatePickerProps> = ({
  id,
  label,
  name,
  error,
  control,
  validation = {},
  format = 'DD-MM-YYYY',
  inputVariant = 'outlined',
  leftArrowIcon,
  leftArrowButtonProps,
  rightArrowIcon,
  rightArrowButtonProps,
  allowKeyboardControl,
  animateYearScrolling,
  autoOk,
  disabled,
  disableFuture,
  disablePast,
  disableToolbar,
  invalidDateMessage,
  KeyboardButtonProps,
  keyboardIcon = <img src={Calendar} alt="calendar" />,
  maxDate,
  maxDateMessage,
  minDate,
  minDateMessage,
  onMonthChange,
  onYearChange,
  openTo,
  orientation,
  readOnly,
  variant,
  cancelLabel,
  clearable,
  clearLabel,
  DialogProps,
  okLabel,
  showTodayButton,
  todayLabel,
  disabledDates = [],
  py = { sm: 1, md: 1.5 },
  showErrorOnTouch = false,
  showErrorOnSubmit = true
}) => {
  const classes = useStyles();
  const { isSubmitted } = useFormState({ control });
  const getValue = (value: any) => {
    return value || null;
  };
  const shouldDisableDate = (day: any) => {
    return disabledDates.some(str => {
      const date = moment(str);
      return (
        date.date() === day.date() &&
        date.month() === day.month() &&
        date.year() === day.year()
      );
    });
  };
  return (
    <Box py={py}>
      {label && (
        <label htmlFor={name}>
          <Text className={classes.label}>{label}</Text>
        </label>
      )}
      <Box className={error ? classes.danger : ''} my={0.5}>
        <Controller
          name={name}
          control={control}
          rules={validation}
          render={({ field: { onChange, onBlur, value, name } }) => (
            <MuiThemeProvider theme={theme}>
              <KeyboardDatePicker
                className={
                  ((showErrorOnSubmit && isSubmitted) || showErrorOnTouch) &&
                  error
                    ? 'invalid'
                    : ''
                }
                id={id}
                format={format}
                name={name}
                value={getValue(value)}
                onBlur={onBlur}
                onChange={e => {
                  const formattedValue = e?.format('YYYY-MM-DD') ?? e;
                  return onChange(formattedValue);
                }}
                InputProps={{ classes: { input: classes.input } }}
                inputVariant={inputVariant}
                leftArrowIcon={leftArrowIcon}
                rightArrowIcon={rightArrowIcon}
                leftArrowButtonProps={leftArrowButtonProps}
                rightArrowButtonProps={rightArrowButtonProps}
                allowKeyboardControl={allowKeyboardControl}
                animateYearScrolling={animateYearScrolling}
                autoOk={autoOk}
                disabled={disabled}
                disableFuture={disableFuture}
                disablePast={disablePast}
                disableToolbar={disableToolbar}
                invalidDateMessage={invalidDateMessage}
                KeyboardButtonProps={KeyboardButtonProps}
                keyboardIcon={keyboardIcon}
                maxDate={maxDate}
                maxDateMessage={maxDateMessage}
                minDate={minDate}
                minDateMessage={minDateMessage}
                onMonthChange={onMonthChange}
                onYearChange={onYearChange}
                openTo={openTo}
                orientation={orientation}
                readOnly={readOnly}
                shouldDisableDate={shouldDisableDate}
                variant={variant}
                cancelLabel={cancelLabel}
                clearable={clearable}
                clearLabel={clearLabel}
                DialogProps={DialogProps}
                okLabel={okLabel}
                showTodayButton={showTodayButton}
                todayLabel={todayLabel}
              />
            </MuiThemeProvider>
          )}
        />
      </Box>
      {((showErrorOnSubmit && isSubmitted) || showErrorOnTouch) && error && (
        <Text className={clsx(classes.label, classes.error)}>
          {error.message}
        </Text>
      )}
    </Box>
  );
};

export default AppDatePicker;
